import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ColGroupDef,
  GridState,
  PostSortRowsParams
} from "ag-grid-enterprise";
import { useConditionalFormatting } from "components/conditional-formatting/use-conditional-formatting";
import { PrintViewLayoutState } from "./model";
import "./style.scss";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import { TExportEventType } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { designScopeExportEventType } from "pages/design-scope-page/shared/design-scope-export-audit";
import { designScopeCatalogExportEventType } from "pages/subscription-settings/module-settings/bootstrap-design-scope/export-design-scope-catalog-audit";
import { COMMENTS_EXPORT_COLUMN_ID } from "utils/comments-export";
import { PrintCommentsCellRenderer } from "./print-cell-renderers";

const PRINT_EXPORT_EVENT_TYPE: Record<string, TExportEventType> = {
  "submittal-log": "SUBMITTAL_LOG_EXPORT_PDF",
  "material-log": "MATERIAL_LOG_EXPORT_PDF",
  "bid-package-log": "BID_PACKAGE_LOG_EXPORT_PDF",
  "custom-material-report": "CUSTOM_MATERIAL_REPORT_EXPORT_PDF",
  "activity-log": "ACTIVITY_LOG_EXPORT_PDF"
};

const PRINT_EXPORT_FEATURE_TYPE: Record<string, string> = {
  "submittal-log": "SUBMITTALS",
  "bid-package-log": "BID_PACKAGES",
  "activity-log": "PROJECT",
  "design-scope-log": "DESIGN_SCOPE",
  "design-scope-catalog": "DESIGN_SCOPE"
};

/** Narrowest a column may auto-size to before its wrapped header stops reading. */
const HEADER_WRAP_MIN_WIDTH = 80;

/** Zero-width spacer columns cap themselves via maxWidth; never outgrow it. */
const headerWrapMinWidth = (col: any): number => {
  const floor =
    col.maxWidth == null
      ? HEADER_WRAP_MIN_WIDTH
      : Math.min(HEADER_WRAP_MIN_WIDTH, col.maxWidth);
  return Math.max(col.minWidth ?? 0, floor);
};

/** Print layout zeroes pinned containers; strip pin/lock so columns render. */
const sanitizeColDefForPrint = (col: any, wrapHeaderText = false): any => {
  const rest = { ...col };
  delete rest.pinned;
  delete rest.lockPosition;
  if (rest.children?.length) {
    rest.children = rest.children.map((child: any) =>
      sanitizeColDefForPrint(child, wrapHeaderText)
    );
  } else if (wrapHeaderText) {
    rest.minWidth = headerWrapMinWidth(rest);
  }
  return rest;
};

const sanitizeGridStateForPrint = (
  initialState: GridState | undefined,
  forceVisibleColumnIds: string[] = []
): GridState | undefined => {
  if (!initialState) return initialState;
  const rest = { ...initialState };
  delete (rest as { columnPinning?: unknown }).columnPinning;
  if (forceVisibleColumnIds.length && rest.columnVisibility) {
    rest.columnVisibility = {
      ...rest.columnVisibility,
      hiddenColIds: rest.columnVisibility.hiddenColIds.filter(
        (colId) => !forceVisibleColumnIds.includes(colId)
      )
    };
  }
  return rest;
};

type PrintRowHeightApi = {
  getAllDisplayedColumns?: () => {
    getColDef?: () => { autoHeight?: boolean };
  }[];
  resetRowHeights?: () => void;
};

/**
 * Fixed-height rows can be reset after columns resize. Auto-height columns
 * measure themselves; AG Grid warns and ignores resetRowHeights in that mode.
 */
export const resetFixedPrintRowHeights = (api: PrintRowHeightApi) => {
  const usesAutoHeight = api
    .getAllDisplayedColumns?.()
    .some((column) => column.getColDef?.()?.autoHeight);
  if (usesAutoHeight) return;
  api.resetRowHeights?.();
};

/** Keep the v36 print viewport aligned with its auto-sized column content. */
export const shrinkPrintGridToColumnWidths = (
  api: {
    getAllDisplayedColumns: () => { getActualWidth: () => number }[];
  },
  hostElement: HTMLElement | null
) => {
  if (!hostElement) return;

  const totalWidth = api
    .getAllDisplayedColumns()
    .reduce((width, column) => width + column.getActualWidth(), 0);
  if (!totalWidth) return;

  hostElement.style.width = `${totalWidth}px`;
  const rootWrapper =
    hostElement.querySelector<HTMLElement>(".ag-root-wrapper");
  if (rootWrapper) {
    rootWrapper.style.width = `${totalWidth}px`;
  }

  window.dispatchEvent(new Event("resize"));
};

export default function AgGridPrintLayout({
  gridRef,
  state,
  rowData,
  rowLoading,
  columnDefEntity,
  autoGroupColumnDef,
  wrapHeaderText = false,
  treeData = false,
  getDataPath,
  postSortRows,
  getRowId,
  gridContext,
  gqlClient
}: {
  gridRef: React.RefObject<AgGridReact<any>>;
  rowData?: any[] | null | undefined;
  rowLoading?: boolean;
  state: PrintViewLayoutState | null;
  columnDefEntity: (ColDef<any> | ColGroupDef<any>)[];
  autoGroupColumnDef?: ColDef<any, any> | undefined;
  /** Design Scope hierarchy print: same tree as the log. */
  treeData?: boolean;
  getDataPath?: (data: any) => string[];
  postSortRows?: (params: PostSortRowsParams) => void;
  getRowId?: (params: any) => string;
  /** Live grid context (entity lookups). Stored print state cannot keep functions. */
  gridContext?: any;
  /** GraphQL client for the export audit event. Subscription prints have no
   * project client, so callers can pass the subscription Apollo client. */
  gqlClient?: any;
  /** Wrap header text over lines and widen columns enough that it does not
   * break mid-word — for logs whose columns are mostly empty and would
   * otherwise auto-size down to an unreadable header stub. */
  wrapHeaderText?: boolean;
}) {
  const { gqlClientForProject } = useContext(ProjectContext);
  const { saveAuditLogEvent } = useAuditEventLog(
    gqlClient ?? gqlClientForProject
  );
  const printLayoutHostRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [columnDefs, setColumnDef] = useState<
    (ColDef<any, any> | ColGroupDef<any>)[] | null | undefined
  >();
  const baseDefaultColDef = useMemo(
    () =>
      ({
        sortable: false,
        menuTabs: [],
        editable: false,
        filter: true,
        resizable: true,
        minWidth: 80,
        ...(wrapHeaderText
          ? { wrapHeaderText: true, autoHeaderHeight: true }
          : {})
      }) as ColDef<any>,
    [wrapHeaderText]
  );

  const { conditionalFormattingDefaultColDef: defaultColDef } =
    useConditionalFormatting({
      gridRef,
      defaultColDef: baseDefaultColDef,
      initialRules: state?.conditionalsFormatting ?? [],
      showAppliedHeaderIcon: false
    });

  const printInitialState = useMemo(
    () =>
      sanitizeGridStateForPrint(
        state?.initialState,
        state?.forceVisibleColumnIds
      ),
    [state?.initialState, state?.forceVisibleColumnIds]
  );

  useEffect(() => {
    if (state?.initialState && columnDefEntity) {
      const { hiddenColIds } = state.initialState.columnVisibility || {
        hiddenColIds: []
      };
      const groupedColIds = new Set(state.initialState.rowGroup?.groupColIds);
      const hiddenColIdsArray = [...state.skipColumnIds, ...hiddenColIds];
      const forceVisibleColIds = new Set(state.forceVisibleColumnIds ?? []);

      const columnDefenination = columnDefEntity
        .filter((columnDef: any) => {
          const colId = columnDef.colId || "";
          // Always include row group columns (hidden in main grid but needed for group UI)
          if (columnDef.rowGroup || groupedColIds.has(colId)) return true;
          if (forceVisibleColIds.has(colId)) return true;
          return !hiddenColIdsArray.includes(colId);
        })
        .map((col: any) => {
          const colId = col.colId || "";
          const c = forceVisibleColIds.has(colId)
            ? { ...col, hide: false }
            : col;
          if (colId === "linked_submittals") {
            return sanitizeColDefForPrint(
              {
                ...c,
                minWidth: 375,
                width: 375,
                suppressAutoSize: true,
                resizable: true
              },
              wrapHeaderText
            );
          }

          // Activity log Details prints with wrap + autoHeight; keep a wrap-
          // friendly width instead of auto-sizing to the full single-line text.
          if (
            state?.pageId === "activity-log" &&
            colId === "details" &&
            c.wrapText
          ) {
            return sanitizeColDefForPrint(
              {
                ...c,
                width: c.maxWidth ?? 420,
                suppressAutoSize: true,
                resizable: true
              },
              wrapHeaderText
            );
          }

          // Material / custom-report Comments: keep a wrap-friendly width so
          // autoHeight can grow the row for every comment line.
          if (colId === COMMENTS_EXPORT_COLUMN_ID && c.autoHeight) {
            return sanitizeColDefForPrint(
              {
                ...c,
                width: c.width ?? 360,
                wrapText: true,
                autoHeight: true,
                cellRenderer: PrintCommentsCellRenderer,
                suppressAutoSize: true,
                resizable: true
              },
              wrapHeaderText
            );
          }

          return sanitizeColDefForPrint(
            {
              ...c,
              minWidth: 0,
              resizable: true
            },
            wrapHeaderText
          );
        });
      setColumnDef(columnDefenination);
    }
  }, [columnDefEntity, state, wrapHeaderText]);

  useEffect(() => {
    const after = () => {
      const isDesignScopeLog = state?.pageId === "design-scope-log";
      const isDesignScopeCatalog = state?.pageId === "design-scope-catalog";
      const treeSystem = state?.treeSystem === "MF" ? "MF" : "UF";
      let eventType: TExportEventType =
        PRINT_EXPORT_EVENT_TYPE[state?.pageId ?? ""] ?? "UNKNOWN_EXPORT_PDF";
      let filename = "ConstructivIQ.pdf";
      if (isDesignScopeLog) {
        eventType = designScopeExportEventType(
          "PDF",
          state?.hierarchy ? "hierarchy" : "scope",
          treeSystem
        );
        filename = `${state?.pdfFileBaseName || "Design_Scope_Log"}.pdf`;
      } else if (isDesignScopeCatalog) {
        eventType = designScopeCatalogExportEventType(
          "PDF",
          state?.hierarchy ? "hierarchy" : "list",
          treeSystem
        );
        filename = `${state?.pdfFileBaseName || "Design_Scope_Catalog"}.pdf`;
      }
      saveAuditLogEvent({
        eventType,
        featureType:
          PRINT_EXPORT_FEATURE_TYPE[state?.pageId ?? ""] ?? "MATERIALS",
        filename
      });
    };

    window.addEventListener("afterprint", after);

    return () => {
      window.removeEventListener("afterprint", after);
    };
  }, [
    saveAuditLogEvent,
    state?.hierarchy,
    state?.pageId,
    state?.pdfFileBaseName,
    state?.treeSystem
  ]);

  const printAutoGroupColumnDef = useMemo(() => {
    if (!autoGroupColumnDef) return undefined;
    const baseCellStyle =
      typeof autoGroupColumnDef.cellStyle === "object"
        ? autoGroupColumnDef.cellStyle
        : {};
    return sanitizeColDefForPrint(
      {
        ...autoGroupColumnDef,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          ...baseCellStyle
        }
      },
      wrapHeaderText
    );
  }, [autoGroupColumnDef, wrapHeaderText]);

  if (!gridRef || !state) {
    return null;
  }

  return (
    <div
      ref={printLayoutHostRef}
      className="relative ag-theme-alpine print-view-layout"
    >
      <AgGridReact
        ref={gridRef}
        initialState={printInitialState}
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        autoGroupColumnDef={printAutoGroupColumnDef ?? autoGroupColumnDef}
        rowData={rowData}
        context={gridContext ?? state?.context}
        treeData={treeData}
        getDataPath={getDataPath}
        postSortRows={postSortRows}
        getRowId={getRowId}
        enableCellSpan={state?.enableCellSpan}
        headerHeight={state?.headerHeight}
        rowHeight={state?.rowHeight}
        domLayout="print"
        groupSuppressBlankHeader={!!autoGroupColumnDef}
        loading={rowLoading || isLoading}
        onGridReady={(event) => {
          if (printInitialState) {
            event.api?.setState(printInitialState);
          }
          event.api.onFilterChanged();
        }}
        onFirstDataRendered={(event) => {
          setTimeout(() => {
            if (printInitialState) {
              event.api?.setState(printInitialState);
            }
            event.api.onFilterChanged();
            event.api?.expandAll();
            event.api.autoSizeColumns({
              skipHeader: true,
              defaultMaxWidth: 300
            });
            // Fixed row heights only. Auto-height columns measure themselves.
            resetFixedPrintRowHeights(event.api);
            setIsLoading(false);
          }, 2000);
        }}
        onColumnResized={(event) => {
          if (event.finished) {
            shrinkPrintGridToColumnWidths(
              event.api,
              printLayoutHostRef.current
            );
            resetFixedPrintRowHeights(event.api);
          }
        }}
        // masterDetail={state.masterDetail}
        groupDefaultExpanded={-1}
        // detailCellRenderer={state.detailCellRenderer}
        // detailRowAutoHeight={state.detailRowAutoHeight}
        suppressMovableColumns
        suppressContextMenu
        suppressMaxRenderedRowRestriction
        suppressColumnVirtualisation
        suppressRowVirtualisation
      />
    </div>
  );
}
